import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { db } from './firebase';

const DOC_TYPE_LABELS = {
  si: 'Sailing Instructions',
  course_card: 'Course Card',
  chart: 'Chart',
  nor: 'Notice of Race',
  programme: 'Programme',
  schedule: 'Race times',
  other_doc: 'Other Document',
  weather: 'Weather',
  tides: 'Tides',
  results: 'Results',
  other_link: 'Other Link',
};

export { DOC_TYPE_LABELS };

/** Load all documents for a club. */
export async function loadClubDocuments(clubId) {
  const snap = await getDocs(collection(db, 'clubs', clubId, 'documents'));
  return Object.fromEntries(snap.docs.map((d) => [d.id, { id: d.id, ...d.data() }]));
}

/** Load season data. */
export async function loadSeason(clubId, seasonId) {
  const snap = await getDoc(doc(db, 'clubs', clubId, 'events', seasonId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/** Load bundle for a season (by content_version). */
export async function loadBundle(clubId, seasonId, contentVersion) {
  if (!contentVersion) return null;
  const ref = doc(db, 'clubs', clubId, 'events', seasonId, 'bundle', `v${contentVersion}`);
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
}

/** Load racetime variants for a season. */
export async function loadRacetimeVariants(clubId, seasonId) {
  try {
    const snap = await getDocs(
      collection(db, 'clubs', clubId, 'events', seasonId, 'racetime_variants'),
    );
    return Object.fromEntries(
      snap.docs.map((d) => [d.id, { id: d.id, ...d.data() }]),
    );
  } catch {
    return {};
  }
}

/** Create a new document and add it to the current season. */
export async function createDocument(clubId, seasonId, { id, title, type, isFree, url }) {
  const ref = doc(db, 'clubs', clubId, 'documents', id);
  const existing = await getDoc(ref);
  if (existing.exists()) throw new Error(`Document ID "${id}" already exists`);
  await setDoc(ref, {
    title,
    type,
    type_label: DOC_TYPE_LABELS[type] || type,
    // Documents default to free (ra-hdil.1): the app suppresses `is_free:false`
    // docs for users without the club content entitlement, so an omitted flag
    // must never silently paywall a NoR. Marking a doc paid is deliberate.
    is_free: isFree ?? true,
    url: url || null,
    document_set_ids: [],
    season_ids: [seasonId],
  });
  await updateDoc(doc(db, 'clubs', clubId, 'events', seasonId), {
    documents: arrayUnion(id),
  });
}

/** Update document metadata. */
export async function updateDocument(clubId, docId, { title, type, isFree, url }) {
  await updateDoc(doc(db, 'clubs', clubId, 'documents', docId), {
    title,
    type,
    type_label: DOC_TYPE_LABELS[type] || type,
    is_free: isFree ?? true,
    url: url || null,
  });
}

/** Delete a document and remove it from the season. */
export async function deleteDocument(clubId, seasonId, docId) {
  await Promise.all([
    deleteDoc(doc(db, 'clubs', clubId, 'documents', docId)),
    updateDoc(doc(db, 'clubs', clubId, 'events', seasonId), {
      documents: arrayRemove(docId),
    }),
  ]);
}

/** Link/unlink a document to a document set. */
export async function toggleDocSetLink(clubId, seasonId, docId, docSetId, currentlyLinked) {
  const ref = doc(db, 'clubs', clubId, 'documents', docId);
  if (currentlyLinked) {
    await updateDoc(ref, { document_set_ids: arrayRemove(docSetId) });
  } else {
    await updateDoc(ref, {
      document_set_ids: arrayUnion(docSetId),
      season_ids: arrayUnion(seasonId),
    });
  }
}

/** Add/remove a document from the current season. */
export async function toggleSeasonDoc(clubId, seasonId, docId, inSeason) {
  const ref = doc(db, 'clubs', clubId, 'events', seasonId);
  if (inSeason) {
    await updateDoc(ref, { documents: arrayRemove(docId) });
  } else {
    await updateDoc(ref, { documents: arrayUnion(docId) });
  }
}
